import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Socket } from "socket.io-client";
import {
  getChatPartnerId,
  getConversationListByType,
  getUserId,
} from "../functions";
import {
  ChatListType,
  ChatMessage,
  ChatUserMetaData,
  ConversationListByType,
  Member,
  MembersGroupInfo,
  Messages,
  PaginationType,
  Player,
  UserMetaData,
} from "../types";
import { ConversationRole, ConversationTypeEnum } from "../types/enums";
import { conversationTypeList } from "../constants";

export interface ChatsState {
  conversationListByType: ConversationListByType;
  totalConversationList: number;
  pagination: PaginationType;
  selectedChat: ChatListType;
  playerById: ChatUserMetaData;
  usersMetaData: UserMetaData;
  personalMessage: Messages;
  adjustedMessages: Messages;
  socketIo: Socket | undefined;
  totalPersonalMessage: number;
  members: Member[];
  players: Player[];
  channel: ChatListType[];
  group: ChatListType[];
  isAdmin: boolean;
  isOpen: boolean;
  isLoading: boolean;
  isBaseLoading: boolean;
  isLoadingMessages: boolean;
  isLoadingConversationList: boolean;
}
export const initialChatState: ChatsState = {
  conversationListByType: {
    [ConversationTypeEnum.PERSONAL]: [],
    [ConversationTypeEnum.GROUP]: [],
    [ConversationTypeEnum.CHANNEL]: [],
  },
  totalConversationList: 0,
  pagination: {} as PaginationType,
  selectedChat: {} as ChatListType,
  playerById: {} as ChatUserMetaData,
  usersMetaData: {} as UserMetaData,
  personalMessage: {} as Messages,
  adjustedMessages: {} as Messages,
  socketIo: {} as Socket,
  totalPersonalMessage: 0,
  members: [] as Member[],
  players: [] as Player[],
  channel: [] as ChatListType[],
  group: [] as ChatListType[],
  isAdmin: false,
  isOpen: false,
  isLoading: false,
  isBaseLoading: false,
  isLoadingMessages: false,
  isLoadingConversationList: false,
};

const chatsSlice = createSlice({
  name: "chats",
  initialState: initialChatState,
  reducers: {
    startCall: (state) => {
      state.totalConversationList = 0;
      state.isAdmin = false;
      state.isOpen = false;
      state.isLoading = false;
      state.isBaseLoading = false;
      state.isLoadingMessages = false;
      state.isLoadingConversationList = false;
      state.usersMetaData = {} as UserMetaData;
      state.pagination = {} as PaginationType;
      state.selectedChat = {} as ChatListType;
      state.playerById = {} as ChatUserMetaData;
      state.members = [] as Member[];
      state.players = [] as Player[];
      state.channel = [] as ChatListType[];
      state.group = [] as ChatListType[];
    },
    toggleDrawer: (state) => {
      state.isOpen = false;
      state.isLoading = false;
      state.isBaseLoading = false;
      state.isLoadingMessages = false;
      state.isLoadingConversationList = false;
      state.pagination = {} as PaginationType;
      state.playerById = {} as ChatUserMetaData;
      state.members = [] as Member[];
      state.players = [] as Player[];
      state.channel = [] as ChatListType[];
      state.group = [] as ChatListType[];
    },
    loading: (state, action) => {
      state.isLoading = action.payload;
    },
    baseLoading: (state, action) => {
      state.isBaseLoading = action.payload;
    },
    loadingConversationList: (state, action) => {
      state.isLoadingConversationList = action.payload;
    },
    loadingMessages: (state, action) => {
      state.isLoadingMessages = action.payload;
    },
    setSocketIo: (state, action) => {
      state.socketIo = action.payload;
    },
    setChatState: (state, action) => {
      state.isOpen = action.payload;
    },
    clearSelectedChat: (state) => {
      state.selectedChat = {} as ChatListType;
    },
    setSelectedChat: (state, action) => {
      state.members = [];
      if (Object.keys(action.payload).length === 0) {
        state.selectedChat = {} as ChatListType;
        return;
      }
      if (!conversationTypeList.includes(action.payload?.type)) return;
      const conversationList =
        state.conversationListByType?.[action.payload?.type]?.length > 0
          ? [...state.conversationListByType[action.payload?.type]]
          : [];
      const conversationIndex = conversationList?.findIndex(
        (conversation) => conversation.chatId === action.payload?.chatId
      );
      if (conversationIndex !== -1) {
        state.conversationListByType[action.payload.type][conversationIndex] = {
          ...conversationList[conversationIndex],
          unread: 0,
        };
      } else {
        const conversationsSelectedType =
          state.conversationListByType?.[action.payload.type];
        state.conversationListByType = {
          ...state.conversationListByType,
          [action.payload.type]:
            conversationsSelectedType?.length > 0
              ? [...conversationsSelectedType, action.payload]
              : [action.payload],
        };
      }
      state.selectedChat = action.payload;
    },
    viewSelectedChat: (state, action) => {
      state.selectedChat = action.payload;
    },
    setPlayerById: (state, action) => {
      state.playerById = action.payload;
    },

    conversationsFetched: (state, action) => {
      const conversationListByType = getConversationListByType(
        action.payload.conversationList
      );
      Object.keys(conversationListByType).forEach(
        (conversationType: string) =>
          (state.conversationListByType[conversationType] =
            action.payload.page === 0
              ? [...conversationListByType[conversationType]]
              : [
                  ...(state.conversationListByType?.[conversationType] ?? []),
                  ...conversationListByType[conversationType],
                ])
      );

      state.totalConversationList = Object.values(
        state.conversationListByType
      ).flat()?.length;
      state.pagination = action?.payload.pagination;
    },
    updatedOnlineConversationList: (state, action) => {
      const personalConversation =
        state.conversationListByType?.[ConversationTypeEnum.PERSONAL];
      let conversationList =
        personalConversation?.length > 0 ? [...personalConversation] : [];
      const data = action.payload;
      let updatedList = false;

      conversationList?.forEach((conversation: ChatListType, index) => {
        const statusOnline = data[getUserId(conversation)];
        if (statusOnline !== conversation?.user?.isOnline) {
          updatedList = true;
          conversationList[index] = {
            ...conversation,
            user: {
              ...conversation?.user,
              photoFile: conversation?.user?.photoFile ?? "",
              isOnline: statusOnline,
            },
          };
        }
      });
      if (updatedList)
        state.conversationListByType[ConversationTypeEnum.PERSONAL] =
          conversationList;
    },
    addNewConversationToConversationList: (state, action) => {
      const conversationChanged: ChatListType = action.payload.message;
      let conversationList = [
        ...Object.values(state.conversationListByType).flat(),
      ];
      const conversationIndex = conversationList.findIndex(
        (pre) => pre?._id === conversationChanged?._id
      );

      if (conversationChanged.type !== ConversationTypeEnum.PERSONAL) {
        if (conversationIndex > -1) {
          conversationList[conversationIndex] = {
            ...conversationChanged,
            membership: {
              ...conversationList[conversationIndex]?.membership,
              role:
                conversationList[conversationIndex]?.membership?.role ??
                ("member" as ConversationRole),
            },
            unread: conversationList[conversationIndex]?.unread,
          };
        } else {
          const membership = {
            ...conversationChanged?.membership,
            role:
              conversationChanged?.membership?.role ??
              ("member" as ConversationRole),
          };
          conversationList = [
            ...conversationList,
            {
              ...conversationChanged,
              membership,
              chatName: conversationChanged.name,
            },
          ];
        }
      } else {
        const chatPartnerId = getChatPartnerId(
          action.payload.userInfoId,
          conversationChanged.participants[0],
          conversationChanged.participants[1]
        );
        if (conversationIndex !== -1) {
          conversationList[conversationIndex] = {
            ...conversationChanged,
            user: conversationList[conversationIndex]?.user,
            unread: conversationList[conversationIndex]?.unread,
          };
        } else {
          conversationList = [
            ...conversationList,
            {
              ...conversationChanged,
              user: state.usersMetaData?.[chatPartnerId],
              unread: 0,
            },
          ];
        }
      }
      const sortedConversationList = conversationList.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      state.conversationListByType = getConversationListByType(
        sortedConversationList
      );
      state.totalConversationList = sortedConversationList?.length ?? 1;
    },
    setMetaData: (state, action) => {
      state.usersMetaData = {
        ...state.usersMetaData,
        [action.payload?.id]: action.payload,
      };
    },
    setUserFromMetaData: (state, action) => {
      const indexConversation = state.conversationListByType?.[
        ConversationTypeEnum.PERSONAL
      ]?.findIndex(
        (conversation) => conversation?.chatId === action.payload.chatId
      );
      if (indexConversation > -1) {
        state.conversationListByType[ConversationTypeEnum.PERSONAL][
          indexConversation
        ].user = action.payload.user;
      }
    },
    setMessages: (state, action) => {
      state.totalPersonalMessage = action.payload?.total;
      const messages = action.payload?.messages as ChatMessage[];
      const chatId = action.payload?.chatId ?? "";
      state.personalMessage = {
        ...state.personalMessage,
        [chatId]:
          action.payload?.page === 0
            ? messages
            : [...(state.personalMessage[chatId] ?? []), ...messages],
      };
    },
    setEmptyAdjustedMessages: (state) => {
      state.adjustedMessages = {};
    },
    setAdjustedMessages: (state) => {
      // Filtered messages are mapped to have date as Date object
      // and sorted in ascending order of their creation time

      const messagesOfSelectedChat: ChatMessage[] =
        state.personalMessage[state.selectedChat?.chatId];

      // Initialize finalData with filteredByTime
      let finalData: Messages = {};
      if (messagesOfSelectedChat?.length > 0) {
        const msgs = messagesOfSelectedChat
          .map((msg) => ({
            ...msg,
            date: new Date(msg.createdAt),
          }))
          .sort((a, b) => a.date.getTime() - b.date.getTime());

        // A Set to hold unique day keys and an array to hold Date objects
        let allDaysSet: Set<string> = new Set();
        let allDays: Date[] = [];

        // Iterate over each message, add the current message's day to allDaysSet and allDays if not already present
        msgs.forEach((msg: ChatMessage & { date: Date }) => {
          const dayKey = msg.date.toISOString().split("T")[0];
          if (!allDaysSet.has(dayKey)) {
            allDaysSet.add(dayKey);
            allDays.push(msg.date);
          }
        });

        // Construct an initial final data object with structure: {"date": []}
        const filteredByTime = allDays.reduce(
          (a, v) => ({ ...a, [v.toISOString().split("T")[0]]: [] }),
          {}
        );

        finalData = filteredByTime;

        // Append each message to the correct date key in finalData
        msgs.forEach((msg: ChatMessage & { date: Date }) => {
          const dayKey = msg.date.toISOString().split("T")[0];
          if (finalData.hasOwnProperty(dayKey)) {
            finalData[dayKey].push(msg);
          }
        });
      }

      // set final grouped by date data
      state.adjustedMessages = finalData;
    },
    setNewMessage: (state, action) => {
      const message = action.payload as ChatMessage;
      const chatId = message?.chatId ?? "";
      state.personalMessage = {
        ...state.personalMessage,
        [chatId]: state.personalMessage[chatId]
          ? [message, ...state.personalMessage[chatId]]
          : [message],
      };
      state.totalPersonalMessage = state.personalMessage[chatId]?.length ?? 1;
    },
    editMessage: (state, action) => {
      state.personalMessage = {
        ...state.personalMessage,
        [action.payload.chatId]: state.personalMessage[
          action.payload.chatId
        ].map((message) => {
          if (message._id === action.payload._id) {
            return {
              ...message,
              ...action.payload,
            };
          }
          return message;
        }),
      };
    },
    setPlayers: (state, action) => {
      state.players =
        action.payload.page === 1
          ? action.payload.players
          : [...state.players, ...action.payload.players];
    },
    setMembers: (state, action) => {
      state.members =
        action.payload.page === 0
          ? action.payload.members
          : [...state.members, ...action.payload.members];
    },
    setChatListByType: (state, action) => {
      const chatList: ChatListType[] = action.payload.data;
      if (action.payload.type) {
        state[action.payload.type] =
          action.payload.page === 0
            ? chatList
            : [...state[action.payload.type], ...chatList];
      } else {
        [ConversationTypeEnum.CHANNEL, ConversationTypeEnum.GROUP].forEach(
          (type) => {
            state[type] =
              action.payload.page === 0
                ? chatList.filter((chat) => chat.type === type)
                : [
                    ...state[type],
                    ...chatList.filter((chat) => chat.type === type),
                  ];
          }
        );
      }
    },
    deletedConversation: (state, action) => {
      if (!conversationTypeList.includes(action.payload?.type)) return;
      const conversationList = state.conversationListByType[
        action.payload.type
      ]?.filter((conversation) => conversation._id !== action.payload._id);
      state.conversationListByType[action.payload.type] = conversationList;
      state.totalConversationList = Object.values(
        state.conversationListByType
      ).flat()?.length;
    },
    kickedConversation: (state, action) => {
      const members = state.members.filter(
        (conversation) => conversation.userId !== action.payload.memberId
      );
      state.members = members;
    },
    updateMemberGroup: (state, action) => {
      const memberGroup = action.payload as MembersGroupInfo;
      state.conversationListByType?.group?.forEach((conversation, index) => {
        if (conversation._id === memberGroup.groupId) {
          const memberGroupInfo = {
            membersCount: memberGroup?.count,
            membersGroup: memberGroup,
            membersIsOnline: Object.values(memberGroup?.onlineUsers).filter(
              Boolean
            ).length,
          };

          state.conversationListByType.group[index] = {
            ...conversation,
            ...memberGroupInfo,
          };
          if (
            Object.keys(state.selectedChat)?.length > 0 &&
            state.selectedChat?._id === conversation._id
          ) {
            state.selectedChat = {
              ...state.selectedChat,
              ...memberGroupInfo,
            };
          }
          return;
        }
      });
    },
    subscribedInConversation: (state, action) => {
      if (!conversationTypeList.includes(action.payload?.type)) return;
      const conversationList =
        state.conversationListByType?.[action.payload.chat.type]?.length > 0
          ? [
              ...state.conversationListByType[action.payload.chat.type],
              action.payload.chat,
            ]
          : [action.payload.chat];
      state.conversationListByType[action.payload.chat.type] = conversationList;
    },
    replaceMessageFromConversation: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        message: string;
      }>
    ) => {
      const { conversationId, messageId, message } = action.payload;

      const messageIndex = state.personalMessage?.[conversationId]?.findIndex(
        (conv) => conv._id === messageId
      );

      state.personalMessage[conversationId][messageIndex] = {
        ...state.personalMessage[conversationId][messageIndex],
        message,
      };
    },
    setIsAdmin: (state, action) => {
      state.isAdmin = action.payload?.isAdmin;
    },
    updateMembershipSeletcedChat: (state, action) => {
      state.selectedChat = {
        ...state.selectedChat,
        membership: action.payload,
      };
    },
    updateIsMutedMembers: (state, action) => {
      const memberIndex = state.members.findIndex(
        (member: Member) => member?.userId === action.payload.userId
      );
      if (memberIndex > -1) {
        state.members[memberIndex] = {
          ...state.members[memberIndex],
          isMuted: action.payload.isMuted,
        };
      }
    },
    setTranslatedMessage: (state, action) => {
      const messageId = action.payload.messageId;
      const chatId = action.payload.chatId;
      const responseTranslate = action.payload.responseTranslate;
      const lang = action.payload.lang ?? responseTranslate?.language ?? "en";

      const conversationMessages = state.personalMessage[chatId]?.map(
        (message) => {
          let selectedMessage = message;
          if (message._id === messageId) {
            selectedMessage = {
              ...message,
              detectedLanguage:
                responseTranslate?.sourceLang ?? message.detectedLanguage,
              translations: {
                ...message.translations,
                [lang]: responseTranslate?.translatedText,
              },
            };
          }
          return selectedMessage;
        }
      );

      state.personalMessage = {
        ...state.personalMessage,
        [chatId]: conversationMessages,
      };
    },
  },
});

export { chatsSlice };
